/*
 * Horizontal timeline layout (pure — no Office.js imports, unit-tested).
 *
 * Spine line vertically centered in the region, milestone circles evenly
 * spaced along it, and alternating above/below text blocks (date, label,
 * optional detail) connected to the circles by short stem lines.
 */

import {
  BoxPrim,
  DEFAULT_PALETTE,
  DEFAULT_REGION,
  LinePrim,
  Palette,
  Primitive,
  Region,
  TextPrim,
} from "./primitives";

export interface TimelineEvent {
  date?: string;
  label: string;
  detail?: string;
}

/* Layout constants (points). */
const INSET = 40; // first/last milestone inset from the region edges
const RADIUS = 9; // milestone circles are 18pt diameter
const STEM = 24; // stem line from circle to text block
const DATE_H = 16;
const GRAY = "64748B";
const STEM_COLOR = "94A3B8";

/* Estimate wrapped line count for text at a given width/font (points).
 * chars-per-line ≈ width ÷ (0.55 × fontPt); honors explicit newlines. */
function estLines(text: string, widthPt: number, fontPt: number): number {
  if (!text) return 1;
  const cpl = Math.max(1, Math.floor(widthPt / (0.55 * fontPt)));
  let lines = 0;
  for (const seg of String(text).split("\n")) lines += Math.max(1, Math.ceil(seg.length / cpl));
  return lines;
}

/* Rendered box height for a line count at a font size (1.35 leading + small pad). */
function boxHeight(lines: number, fontPt: number): number {
  return Math.ceil(lines * 1.35 * fontPt) + 4;
}

export function timelineLayout(events: TimelineEvent[], region?: Region, palette?: Palette): Primitive[] {
  const reg = region || DEFAULT_REGION;
  const pal = palette || DEFAULT_PALETTE;
  if (!events || events.length === 0) return [];

  const midY = reg.y + reg.h / 2;
  const spacing = events.length > 1 ? (reg.w - 2 * INSET) / (events.length - 1) : 0;
  const textW = events.length > 1 ? Math.min(150, spacing * 0.9) : 150;

  const prims: Primitive[] = [];
  prims.push({
    kind: "line",
    x1: reg.x,
    y1: midY,
    x2: reg.x + reg.w,
    y2: midY,
    style: { lineColor: pal.primary, lineWeight: 2.5 },
  } as LinePrim);

  for (let i = 0; i < events.length; i++) {
    const e = events[i];
    const cx = events.length > 1 ? reg.x + INSET + i * spacing : reg.x + reg.w / 2;
    const above = i % 2 === 0;

    // Milestone circle on the spine.
    prims.push({
      kind: "ellipse",
      x: cx - RADIUS,
      y: midY - RADIUS,
      w: RADIUS * 2,
      h: RADIUS * 2,
      style: { fill: pal.accent },
    } as BoxPrim);

    // Stem from the circle edge toward the text block.
    const stemFrom = above ? midY - RADIUS : midY + RADIUS;
    const stemTo = above ? stemFrom - STEM : stemFrom + STEM;
    prims.push({
      kind: "line",
      x1: cx,
      y1: stemFrom,
      x2: cx,
      y2: stemTo,
      style: { lineColor: STEM_COLOR, lineWeight: 1 },
    } as LinePrim);

    // Real (wrapped) heights so an above-axis block's true bottom lands on
    // stemTo — fixed assumed heights let long descriptions overflow onto the node.
    const dateH = e.date ? DATE_H : 0;
    const labelH = boxHeight(estLines(e.label, textW, 11), 11);
    const detailH = e.detail ? boxHeight(estLines(e.detail, textW, 9), 9) : 0;
    const blockH = dateH + labelH + detailH;
    const tx = Math.max(reg.x, Math.min(cx - textW / 2, reg.x + reg.w - textW));
    // above: block bottom pinned to stemTo, growing up (clamped into the region).
    let ty = above ? Math.max(reg.y, stemTo - blockH) : stemTo;
    if (e.date) {
      prims.push({
        kind: "text", x: tx, y: ty, w: textW, h: dateH, text: e.date,
        style: { fontSize: 12, fontColor: pal.primary, bold: true, align: "center" },
      } as TextPrim);
      ty += dateH;
    }
    prims.push({
      kind: "text", x: tx, y: ty, w: textW, h: labelH, text: e.label,
      style: { fontSize: 11, fontColor: pal.ink, align: "center" },
    } as TextPrim);
    ty += labelH;
    if (e.detail) {
      prims.push({
        kind: "text", x: tx, y: ty, w: textW, h: detailH, text: e.detail,
        style: { fontSize: 9, fontColor: GRAY, align: "center" },
      } as TextPrim);
    }
  }
  return prims;
}
